import { inject, Injectable } from '@angular/core';
import { DbService } from '../db/db.service';
import { Order } from '../../models/orders/order';
import { Timestamp } from 'firebase/firestore';
import { CategoryStatistics } from '../../models/orders/categoryStatistics';
import { Statistics } from '../../models/orders/statistics';
import { filterQueryByDate } from '../../utils/utils';
import { environment } from '../../../environments/environment';
import { PaymentMethod } from '../../models/paymentMethod';
import { Product } from '../../models/product';
import { isNoEmptyArray, isObject, isValidFunction } from '@pineapplelab/util';
import { ApiService } from '../api/api.service';
import { OrderCreationDTO } from '../../models/dto/orderPurchaseDTO';
import firebase from "firebase/compat/app";
import { isEmpty } from 'lodash';

@Injectable({
	providedIn: 'root',
})
export class OrderService {

	private readonly db = inject(DbService);
	private readonly api = inject(ApiService);

	private getOrders(projectId: string) {
		return this.db.getProyIdRef(projectId).collection(Order.PATH);
	}

	async getAllOrders(projectId: string): Promise<Order[]> {
		const snapshot = await this.getOrders(projectId).get();
		let orders = snapshot.docs.map((order) => new Order(order.data()));
		return orders;
	}

	async getOrdersWithDateFilters(projectId: string, filters: { from: Date; to: Date }): Promise<Order[]> {
		const snapshot = this.getOrders(projectId);

		const snapWithFilters = filterQueryByDate(snapshot, filters);
		const result = await snapWithFilters.get();

		let orders = result.docs.map((order: any) => new Order(order.data()));
		return orders;
	}

	async getCategoryStatistics(projectId: string, filters: { from: Date; to: Date }): Promise<CategoryStatistics[]> {

		const snapshot = this.db.getProyIdRef(projectId).collection(CategoryStatistics.PATH);

		const snapWithFilters = filterQueryByDate(snapshot, filters);
		const results = await snapWithFilters.get();

		const categoriesStatistics = results.docs.map(
			(category: any) => new CategoryStatistics(category.data())
		);

		return categoriesStatistics;
	}

	async getOrdersStatistics(projectId: string): Promise<Statistics> {
		const snapshot = await this.db.getProyIdRef(projectId)
			.collection(Statistics.PATH)
			.get();
		const statistics = new Statistics(snapshot.docs[0].data());
		return statistics;
	}

	async get(orderId: string): Promise<Order> {
		if (!orderId) {
			throw new Error('orderId undefined');
		}

		const result = await this.db.getProyIdRef(environment.projectId)
			.collection(Order.PATH)
			.doc(orderId)
			.get();

		if (!result.exists) {
			return null;
		}

		return new Order(result.data());
	}

	async purchase(paymentMethod: PaymentMethod, products: Product[]): Promise<Order> {
		if (!paymentMethod || !products || !isNoEmptyArray(products)) {
			throw new Error('invalid purchase params');
		}

		const body = await this.getOrderCreationBody(paymentMethod, products);

		const order = await this.api.post('/payment/create-order', body);
		return new Order(order);
	}

	private async getOrderCreationBody(paymentMethod: PaymentMethod, products: Product[]): Promise<OrderCreationDTO> {
		let paymentDetailsObject = {};
		if (paymentMethod?.__isNew && paymentMethod?.__creditCardData) {
			paymentDetailsObject = paymentMethod?.__creditCardData;
		}
		if (paymentMethod?.orderId) {
			paymentDetailsObject = { refno: paymentMethod?.orderId };
		}
		const billingDetails = await this.getBillingDetails(paymentMethod);

		const result = {
			country: billingDetails?.countryCode,
			currency: "usd",
			billingDetails,
			items: products.map(product => ({ id: product?.id, quantity: 1 })),
			paymentDetails: {
				currency: "usd",
				paymentMethod: paymentDetailsObject,
				// TODO: resolve what should be passed here for production env
				type: "TEST"
			}
		};
		return result;
	}

	observe(
		orderId: string,
		next: (snapshot: firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>) => void,
		error: (error: firebase.firestore.FirestoreError) => void
	): () => void {
		if (!orderId || !isValidFunction(next) || !isValidFunction(error)) {
			return null;
		}

		return this.db.getProyIdRef(environment.projectId).collection(Order.PATH).doc(orderId).onSnapshot(next, error);
	}

	private async getBillingDetails(paymentMethod: PaymentMethod): Promise<any> {
		if (paymentMethod && isObject(paymentMethod?.__billingDetails) && !isEmpty(paymentMethod?.__billingDetails)) {
			return paymentMethod.__billingDetails;
		}
		if (!paymentMethod?.orderId) {
			return null;
		}

		const query = await this.db.getProyIdRef(environment.projectId).collection(Order.PATH).doc(paymentMethod.orderId).get();
		if (!query.exists) {
			return null;
		}
		const order = new Order(query.data());
		return order?.billingAddress;
	}
}
